import type { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { QueryClient, UseQueryResult, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import { VenuePagesRow, getVenuePage } from "../../data/venue_pages";

import { SupabaseClient } from "@supabase/supabase-js";

type UseVenuePageOptions =
  | {
      id: string;
    }
  | {
      slug: string;
    };

export function useVenuePage(options: UseVenuePageOptions): {
  venuePage: VenuePagesRow | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryFn = async (): Promise<VenuePagesRow> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const venuePage = await getVenuePage(supabase, options);
      return venuePage;
    } catch (error) {
      const identifier = "id" in options ? options.id : options.slug;
      const identifierType = "id" in options ? "id" : "slug";

      logException(error as Error, {
        extra: {
          data: `Error fetching venue page inside useVenuePage hook`,
        },
        tags: {
          supabase: true,
          feature: SentryFeatureTags.VenuePages,
          identifierType,
          identifier,
        },
      });
      throw error;
    }
  };

  const identifier = "id" in options ? options.id : options.slug;

  const {
    data: venuePage,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venue-page", identifier],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { venuePage, isLoading, error, refetch };
}

export async function prefetchVenuePage(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  options: UseVenuePageOptions
) {
  const identifier = "id" in options ? options.id : options.slug;

  await queryClient.prefetchQuery({
    queryKey: ["venue-page", identifier],
    queryFn: async () => {
      return await getVenuePage(supabase, options);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Seed the useVenuePage query from a venue page the caller already holds
 * (e.g. the Redis-cached read), so the layout does not query the row twice.
 */
export function hydrateVenuePage(
  queryClient: QueryClient,
  options: UseVenuePageOptions,
  venuePage: VenuePagesRow
) {
  const identifier = "id" in options ? options.id : options.slug;
  queryClient.setQueryData(["venue-page", identifier], venuePage);
}
